import { HttpErrorResponse } from '@angular/common/http';
import { MEETING_TYPE } from '@constants/app.constants';
import { CALENDAR_TYPE } from '@constants/calendar.constants';

export const INTERVAL = {
  MONTHLY: 'monthly',
  YEARLY: 'yearly'
} as const;

export const SUPPORTED_LANGUAGES = {
  DE_CH: 'de_CH',
  EN_US: 'en_US'
} as const;

export const CURRENCY = {
  CHF: 'CHF',
  EUR: 'EUR',
  USD: 'USD',
  GBP: 'GBP'
} as const;

export const RENDER_MODE = {
  DASHBOARD: 'dashboard',
  WEBSITE: 'website',
  SALES: 'sales',
} as const;

export const SUBSCRIPTION = {
  BASIC: 'basic',
  PROFESSIONAL: 'professional',
  BUSINESS: 'business',
  ENTERPRISE: 'enterprise',
  STARTER: 'starter',
  TEAM: 'team'
} as const;

// The currently sellable v2 tiers (JB-1029). The legacy-only tiers
// 'professional' and 'business' were replaced by 'starter'/'team' in v2, so
// they must not be offered as a "popular plan" — only these names render in
// the pricing table.
export const NEW_SUBSCRIPTION_PLANS = [
  SUBSCRIPTION.BASIC,
  SUBSCRIPTION.STARTER,
  SUBSCRIPTION.TEAM,
  SUBSCRIPTION.ENTERPRISE,
] as const;

export const SUBSCRIPTION_ID = {
  basic: 103,
  professional: 102,
  business: 104,
  enterprise: 105,
  // v2 tier set (JB-1029). IDs match config/Seeds/SubscriptionTypesSeed.php in
  // the CakePHP backend. Keys use a `v2_` prefix because legacy 'basic' (103)
  // and v2 'basic' (202) share the same display name — see SUBSCRIPTION_INFO
  // below for the tier-precedence mapping.
  v2_basic: 202,
  v2_starter: 203,
  v2_team: 204,
  v2_enterprise: 205
} as const;

export const SUBSCRIPTION_CONSTANTS = {
  SUBSCRIPTION_FREE_TYPE: {
    id: 1,
    name: 'Free',
    db_name: 'free',
    desc: 'Calenso Free',
    is_deprecated: true
  },
  SUBSCRIPTION_FLATRATE_TYPE: {
    id: 5,
    name: 'flatrate',
    desc: 'Calenso Flatrate',
    is_deprecated: true
  },
  SUBSCRIPTION_WHITELABEL_TYPE: {
    id: 100,
    name: 'whitelable',
    desc: 'Calenso Whitelable',
    is_deprecated: true
  },
  SUBSCRIPTION_ENTERPRISE_TYPE: {
    id: 101,
    name: 'Old Enterprise',
    desc: 'Calenso Enterprise',
    is_deprecated: true
  },
  // Legacy v1 tiers (102-105). Deprecated — replaced by the v2 set (202-205).
  // Existing customers stay on these; they are no longer sellable and render
  // with a "deprecated" badge wherever plans are listed.
  SUBSCRIPTION_BASIC_TYPE: {
    id: 103,
    name: 'Basic',
    db_name: 'basic',
    desc: 'Calenso Basic',
    is_deprecated: true
  },
  SUBSCRIPTION_PROFESSIONAL_TYPE: {
    id: 102,
    name: 'Professional',
    db_name: 'pro',
    desc: 'Calenso professional',
    is_deprecated: true
  },
  SUBSCRIPTION_BUSINESS_TYPE: {
    id: 104,
    name: 'Business',
    db_name: 'business',
    desc: 'Calenso Business',
    is_deprecated: true
  },
  SUBSCRIPTION_NEW_ENTERPRISE_TYPE: {
    id: 105,
    name: 'Enterprise',
    db_name: 'enterprise',
    desc: 'Calenso New Enterprise',
    is_deprecated: true
  },
  // v2 tier set (JB-1029). IDs and db_names match config/Seeds/SubscriptionTypesSeed.php
  // in the CakePHP backend. is_deprecated stays false — these are the current
  // sellable plans, having replaced the now-deprecated legacy 102-105 set.
  SUBSCRIPTION_V2_BASIC_TYPE: {
    id: 202,
    name: 'Basic',
    db_name: 'basic',
    desc: 'Calenso Basic',
    is_deprecated: false
  },
  SUBSCRIPTION_V2_STARTER_TYPE: {
    id: 203,
    name: 'Starter',
    db_name: 'starter',
    desc: 'Calenso Starter',
    is_deprecated: false
  },
  SUBSCRIPTION_V2_TEAM_TYPE: {
    id: 204,
    name: 'Team',
    db_name: 'team',
    desc: 'Calenso Team',
    is_deprecated: false
  },
  SUBSCRIPTION_V2_ENTERPRISE_TYPE: {
    id: 205,
    name: 'Enterprise',
    db_name: 'enterprise',
    desc: 'Calenso Enterprise',
    is_deprecated: false
  }
};

// Tiers selectable as a partner's minimum-subscription floor
// (minimum_required_subscription_plan). Restricted to the v2 set (202-205):
// the pricing component only recognises v2 IDs in PRICING_PLAN_PRECEDENCE, so
// offering a legacy/deprecated tier here would store a floor the pricing table
// silently ignores. Kept as an explicit list (not a deprecated-flag filter) so
// the option set stays correct regardless of how is_deprecated is toggled.
export const MINIMUM_SUBSCRIPTION_PLAN_OPTIONS = [
  SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_V2_BASIC_TYPE,
  SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_V2_STARTER_TYPE,
  SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_V2_TEAM_TYPE,
  SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_V2_ENTERPRISE_TYPE,
];

// Cross-generation tier sets — mirror the backend's SubscriptionTierHelper: each
// v2 plan maps to its legacy equivalent (Starter≈Professional, Team≈Business,
// Enterprise≈Enterprise). Gate features on these sets, never on a single
// generation's ID — that is how the v2 rollout silently locked analytics for
// v2 partners (JB-1482). The FE enterprise set additionally keeps 101
// (Old Enterprise) for parity with existing dashboard gates; the BE set is [105, 205].
export const PROFESSIONAL_TIER_SUBSCRIPTION_IDS = [
  SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_PROFESSIONAL_TYPE.id,
  SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_V2_STARTER_TYPE.id,
];

export const BUSINESS_TIER_SUBSCRIPTION_IDS = [
  SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_BUSINESS_TYPE.id,
  SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_V2_TEAM_TYPE.id,
];

export const ENTERPRISE_TIER_SUBSCRIPTION_IDS = [
  SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_ENTERPRISE_TYPE.id,
  SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_NEW_ENTERPRISE_TYPE.id,
  SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_V2_ENTERPRISE_TYPE.id,
];

export const VALID_SUBSCRIPTION_IDS = [
  SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_FREE_TYPE.id,
  SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_FLATRATE_TYPE.id,
  SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_WHITELABEL_TYPE.id,
  SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_ENTERPRISE_TYPE.id,
  SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_BASIC_TYPE.id,
  SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_PROFESSIONAL_TYPE.id,
  SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_BUSINESS_TYPE.id,
  SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_NEW_ENTERPRISE_TYPE.id,
  // v2 tier set (JB-1029)
  SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_V2_BASIC_TYPE.id,
  SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_V2_STARTER_TYPE.id,
  SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_V2_TEAM_TYPE.id,
  SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_V2_ENTERPRISE_TYPE.id,
];

// Per-currency price for one unit of an extra item. Keep all 4 currencies
// populated even when values are identical — locks the shape so a future
// per-currency tweak is a one-line edit, not a refactor.
export interface ExtraCostMatrix {
  chf: number;
  eur: number;
  usd: number;
  gbp: number;
}

export type ExtraCostItem = 'user' | 'store' | 'group_appointment' | 'conference';

export type BrandExtraCosts = Record<ExtraCostItem, ExtraCostMatrix>;

// Values mirror the v2 add-on prices in the Stripe catalogue (verified
// against Calenso AG TEST/LIVE + JRNI Business TEST/LIVE on 2026-05-15).
// Every add-on is 5/mo · 60/yr flat across CHF/EUR/USD/GBP in both brands.
// If Stripe pricing diverges in future, edit the matching cell only — the
// shape (per-brand × per-currency) is already in place.
const CALENSO_EXTRA_COSTS: BrandExtraCosts = {
  user:              { chf: 5, eur: 5, usd: 5, gbp: 5 },
  store:             { chf: 5, eur: 5, usd: 5, gbp: 5 },
  group_appointment: { chf: 5, eur: 5, usd: 5, gbp: 5 },
  conference:        { chf: 5, eur: 5, usd: 5, gbp: 5 },
};

const JRNI_EXTRA_COSTS: BrandExtraCosts = {
  user:              { chf: 5, eur: 5, usd: 5, gbp: 5 },
  store:             { chf: 5, eur: 5, usd: 5, gbp: 5 },
  group_appointment: { chf: 5, eur: 5, usd: 5, gbp: 5 },
  conference:        { chf: 5, eur: 5, usd: 5, gbp: 5 },
};

const EXTRA_COSTS_BY_BRAND: Record<string, BrandExtraCosts> = {
  Calenso: CALENSO_EXTRA_COSTS,
  JRNI:    JRNI_EXTRA_COSTS,
};

/**
 * Resolves the extra-item unit cost for the active brand + currency.
 * Falls back to Calenso pricing if the brand isn't mapped — defensive,
 * should never happen given environment.brandName is one of the two.
 */
export function getExtraCost(item: ExtraCostItem, currency: string, brand: string): number {
  const brandCosts = EXTRA_COSTS_BY_BRAND[brand] ?? CALENSO_EXTRA_COSTS;
  const key = currency.toLowerCase() as keyof ExtraCostMatrix;
  return brandCosts[item][key];
}

export const VAT_TAX_PERCENTAGE = 8.1;

export const CUSTOM_PLAN_USER_TYPE = 'additional_users';
export const CUSTOM_PLAN_STORE_TYPE = 'additional_stores';
export const CUSTOM_PLAN_GROUP_APPOINTMENT_TYPE = 'additional_events';
export const CUSTOM_PLAN_CONFERENCE_TYPE = 'additional_conferences';

export const ENTERPRISE_REDIRECTION_LINK = 'https://calenso.com/demo';

export const TRIAL_MODE = {
  TIME: 'time',
  BOOKINGS: 'bookings'
};
export const SALES_TOKEN = '82e80dc8-9318-4be5-9be1-e8ef79859fca';

export const PAYMENT_INTENT_RESPONSE_STATUS = {
  PROCESSING: 'processing',
  SUCCEEDED: 'succeeded',
  REQUIRES_PAYMENT_METHOD: 'requires_payment_method',
};

export const STRIPE_SUBSCRIPTION_STATUS = {
  ACTIVE: 'active',
  PAST_DUE: 'past_due',
  CANCELED: 'canceled',
  UNPAID: 'unpaid',
  INCOMPLETE: 'incomplete',
  TRIALING: 'trialing',
  EXPIRED: 'expired',
  PAUSED: 'paused'
} as const;

export const SUBSCRIPTION_INFO: Record<number, { name: string; label: string, precedence: number }> = {
  [SUBSCRIPTION_ID.basic]: {
    name: 'basic',
    label: 'your_subscription_component.basic',
    precedence: 1
  },
  [SUBSCRIPTION_ID.professional]: {
    name: 'professional',
    label: 'your_subscription_component.pro',
    precedence: 2
  },
  [SUBSCRIPTION_ID.business]: {
    name: 'business',
    label: 'your_subscription_component.business',
    precedence: 3
  },
  [SUBSCRIPTION_ID.enterprise]: {
    name: 'enterprise',
    label: 'your_subscription_component.enterprise',
    precedence: 4
  },
  // v2 tier set (JB-1029). Precedence mirrors the legacy tier mapping
  // (Phase 1 features_partners mirror): 202↔103, 203↔102, 204↔104, 205↔105.
  [SUBSCRIPTION_ID.v2_basic]: {
    name: 'basic',
    label: 'your_subscription_component.basic',
    precedence: 1
  },
  [SUBSCRIPTION_ID.v2_starter]: {
    name: 'starter',
    label: 'your_subscription_component.starter',
    precedence: 2
  },
  [SUBSCRIPTION_ID.v2_team]: {
    name: 'team',
    label: 'your_subscription_component.team',
    precedence: 3
  },
  [SUBSCRIPTION_ID.v2_enterprise]: {
    name: 'enterprise',
    label: 'your_subscription_component.enterprise',
    precedence: 4
  },
};

export const CALENDAR_SUBSCRIPTION_INFO = {
  [CALENDAR_TYPE.office365]: 'is_office365_enabled',
  [CALENDAR_TYPE.icloud]: 'is_icloud_enabled',
  [CALENDAR_TYPE.google]: 'is_google_calendar_enabled',
  [CALENDAR_TYPE.exchange]: 'is_exchange_calendar_enabled',
  [CALENDAR_TYPE.caldav]: 'is_caldav_calendar_enabled',
};

export const MEETING_SUBSCRIPTION_INFO = {
  [MEETING_TYPE.PHONE]: 'is_phone_meeting_provider_enabled',
  [MEETING_TYPE.STORE]: 'is_store_meeting_provider_enabled',
  [MEETING_TYPE.ZOOM]: 'is_zoom_meeting_provider_enabled',
  [MEETING_TYPE.CUSTOMER]: 'is_customer_meeting_provider_enabled',
  [MEETING_TYPE.UNBLU]: 'is_unblu_meeting_provider_enabled',
  [MEETING_TYPE.MICROSOFT_TEAMS]: 'is_teams_meeting_provider_enabled',
  [MEETING_TYPE.GOTO_MEETING]: 'is_goto_meeting_meeting_provider_enabled',
  [MEETING_TYPE.WEBEX]: 'is_webex_meeting_provider_enabled',
  [MEETING_TYPE.GOOGLE_MEET]: 'is_google_meet_meeting_provider_enabled',
  [MEETING_TYPE.WILL_CALL_YOU]: 'is_we_will_call_you_meeting_provider_enabled',
  [MEETING_TYPE.METAVERSE]: 'is_metaverse_meeting_provider_enabled',
  [MEETING_TYPE.KMEET]: 'is_kmeet_meeting_provider_enabled',
};

// wire values match the ERROR_CODE constants on the cakephp exceptions
export const CANCEL_SUBSCRIPTION_ERROR_CODE = {
  MANUAL_CANCELLATION_REQUIRED: 'manual_cancellation_required',
  NO_ACTIVE_SUBSCRIPTION: 'no_active_subscription',
  UNSUPPORTED_PAYMENT_PROVIDER: 'unsupported_payment_provider',
} as const;

export const CANCEL_SUBSCRIPTION_FALLBACK_MESSAGE_KEY = 'confirmation_modal_component.cancellation_failed';

// Shown up front when the subscription is invoice-billed and can only be cancelled by
// support — the pre-flight check reuses the same key the backend's error_code maps to,
// so the user reads identical wording whichever path surfaces it.
export const CANCEL_SUBSCRIPTION_MANUAL_REQUIRED_MESSAGE_KEY = 'confirmation_modal_component.cancellation_manual_required';

// Same condition, support-facing wording for the internal CRM: names the actual cause
// (Bexio subscription with no Bexio order document) and the manual step required, which
// a partner-facing message must not do.
export const CANCEL_SUBSCRIPTION_MANUAL_REQUIRED_ADMIN_MESSAGE_KEY = 'confirmation_modal_component.cancellation_manual_required_admin';

export const CANCEL_SUBSCRIPTION_ERROR_MESSAGE_KEYS: Record<string, string> = {
  [CANCEL_SUBSCRIPTION_ERROR_CODE.MANUAL_CANCELLATION_REQUIRED]: CANCEL_SUBSCRIPTION_MANUAL_REQUIRED_MESSAGE_KEY,
  [CANCEL_SUBSCRIPTION_ERROR_CODE.NO_ACTIVE_SUBSCRIPTION]: 'confirmation_modal_component.no_active_subscription',
  [CANCEL_SUBSCRIPTION_ERROR_CODE.UNSUPPORTED_PAYMENT_PROVIDER]: 'confirmation_modal_component.unsupported_payment_provider',
};

// endpoint per payment provider; an unmapped provider is what used to make
// SettingsService.cancelSubscription() return undefined
export const CANCEL_SUBSCRIPTION_PATH_BY_PROVIDER: Record<string, string> = {
  stripe: 'stripe/cancel_subscription',
  bexio: 'bexio/cancel_subscription',
};

export function resolveCancelSubscriptionErrorKey(error: HttpErrorResponse): string {
  const errorCode = error?.error?.errors?.[0]?.error_code;
  return CANCEL_SUBSCRIPTION_ERROR_MESSAGE_KEYS[errorCode] ?? CANCEL_SUBSCRIPTION_FALLBACK_MESSAGE_KEY;
}
